/* eslint-disable max-len */

import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RootState } from 'store';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Checkbox, Col, Divider, Empty, Form, Row, Typography } from 'antd';
import isEqual from 'lodash/isEqual';
import { MdCancel } from 'react-icons/md';
import { AiOutlineEdit } from 'react-icons/ai';
import { PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';
import ProjectSelect from 'modules/project/components/ProjectSelect';
import LinkExternalIcon from 'components/LinkExternalIcon';
import SelectUserAvatar from 'components/SelectUserAvatar';
import SelectResponsible from 'components/TaskModal/components/SelectResponsible';
import SelectFollowers from 'components/TaskModal/components/SelectFollowers';
import RequestTimerDropdown from 'components/Timer/RequestTimerDropdown';
import FormBuilderAnswers from 'components/FormBuilder/FormBuilderAnswers';
import FieldTimeRangeCustom from 'components/FieldTimeRangeCustom';
import FormBuilderWizardForm from 'components/FormBuilder/FormBuilderWizardForm';
import FormItem, { parseFormItemAnswer } from 'components/FormBuilder/FormItem';
import FieldUpload from 'components/FormBuilder/fields/FieldUpload';
import FormButtons from 'components/FormBuilder/FormButtons';
import { getFormData } from 'components/FormBuilder/buttons/FormSubmitButton';
import { convertHrsMinsToMins, convertMinsToHrsMins, parseDateToUSFormat, renderTime } from 'utils/string.utils';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { debounceAsync } from 'utils/debounce';
import { formScrollToFieldOnError } from 'utils/form';
import { mapModelToUploadFileApi } from 'utils/array.utils';
import {
  FormBuilderData,
  FormBuilderFieldDate,
  FormBuilderFieldInput,
  FormBuilderFieldRadio,
  FormBuilderFieldUpload,
  FormBuilderFormData,
} from 'types/formBuilder';
import { UploadFileApi } from 'types/upload-type';
import appRoutes from 'config/appRoutes';
import ENVIRONMENT from 'config/environment';
import { UserResponse } from 'types';
import { RichTextEditorHandle } from 'components/RichTextEditor';
import RequestFormHeader from '../RequestFormHeader';
import style from './index.module.less';

const { Text } = Typography;

interface FixedFields {
  subject: FormBuilderFieldInput;
  deadline: FormBuilderFieldDate;
  skipEstimateApproval: FormBuilderFieldRadio;
  uploads: FormBuilderFieldUpload;
}

const fixedFields: FixedFields = {
  subject: {
    name: 'subject',
    label: 'Request name',
    type: 'input',
    submitRequired: true,
    placeholder: 'Give your request a name',
    maxLength: 60,
    rules: [
      { required: true, message: 'Please enter the request name' },
      { max: 60, message: 'Request name must be 60 characters or less' },
    ],
  } as FormBuilderFieldInput,
  deadline: {
    name: 'target_date',
    label: 'Deadline',
    type: 'date',
    submitRequired: true,
    mode: 'simple',
  },
  skipEstimateApproval: {
    name: 'skip_estimate_approval',
    label: 'Skip estimate approval?',
    type: 'radio',
    mode: 'pill',
    submitRequired: true,
    options: [
      {
        label: "Yes, I'd like to skip the estimate approval and start the project right away.",
        value: true,
      },
      {
        label: 'No, I would like to discuss this with someone further before getting started.',
        value: false,
      },
    ],
  },
  uploads: {
    name: 'uploads',
    label: 'Upload any assets related to this project (optional)',
    type: 'upload',
  },
};

export const parseFormBuilderAnswers = (
  answers: Record<string, any>,
  data: FormBuilderFormData[],
  listForms: FormBuilderData[],
): FormBuilderFormData[] => {
  return data.reduce((acc, item) => {
    if (item.type === 'formGroup') {
      const formGroup = item.fields ?? listForms.find((form) => form.id === item.formId)?.form;

      if (formGroup) {
        acc.push({ ...item, fields: parseFormBuilderAnswers(answers, formGroup, listForms) });
      }
    }

    if ('name' in item) {
      let answer = answers[item.name];

      if (item.type === 'upload' && Array.isArray(answer)) {
        answer = answer
          .filter((file: UploadFileApi) => file.response && !file.response.stated)
          .map((file: UploadFileApi) => file.response?.uuid);
      }

      if (item.type === 'textEditor' && answer) {
        const deltaFound = answers[`${item.name}_delta`] ?? item.answer_delta;

        answer = answer.replaceAll(
          `src="${ENVIRONMENT.UPLOADS_URL.replace('uploads', 'tmp')}/`,
          `src="${ENVIRONMENT.UPLOADS_URL}/`,
        );
        acc.push({
          ...item,
          answer,
          answer_delta: deltaFound,
        });

        return acc;
      }

      acc.push({ ...item, answer });
    }

    return acc;
  }, [] as FormBuilderFormData[]);
};

const getRequestFormData = ({
  type,
  request,
  formData,
}: {
  type: RequestFormProps['type'];
  request: RequestFormProps['request'];
  formData?: FormBuilderFormData[];
}): FormBuilderFormData[] | undefined => {
  if (!formData) return undefined;

  return [
    ...(type === 'create' || type === 'draft'
      ? [
          {
            ...fixedFields.subject,
            answer: request?.[fixedFields.subject.name],
          },
        ]
      : []),
    ...formData,
    {
      ...fixedFields.deadline,
      answer: request?.[fixedFields.deadline.name],
    },
    {
      ...fixedFields.skipEstimateApproval,
      answer: request?.[fixedFields.skipEstimateApproval.name],
    },
  ];
};

export const parseDataToSave = (
  mainValues: Record<string, any>,
  answerValues?: Record<string, any>,
  formBuilder?: FormBuilderData,
  listForms?: FormBuilderData[],
): Record<string, any> => {
  if (answerValues) {
    Object.values(fixedFields).forEach((field) => {
      if (field.name in answerValues) {
        mainValues[field.name] = answerValues[field.name];

        delete answerValues[field.name];
      }
    });
  }

  if (formBuilder && answerValues && listForms) {
    mainValues.description = JSON.stringify({
      ...formBuilder,
      form: formBuilder.form && parseFormBuilderAnswers(answerValues, formBuilder.form, listForms),
      wizardForm:
        formBuilder.wizardForm &&
        formBuilder.wizardForm.map((item) => ({ ...item, form: parseFormBuilderAnswers(answerValues, item.form, listForms) })),
    });

    mainValues.title = formBuilder.name;
  }

  if ('skip_estimate_approval' in mainValues) {
    mainValues.skip_estimate_approval = mainValues.skip_estimate_approval ?? false;
  }

  if ('kickoff_call' in mainValues) {
    mainValues.kickoff_call = mainValues.kickoff_call ?? null;
  }

  if ('subject' in mainValues) {
    mainValues.subject = mainValues.subject || 'Draft';
  }

  if ('estimated_time_range' in mainValues) {
    if (mainValues.estimated_time_range) {
      const [hourMin, hourMax] = mainValues.estimated_time_range.split('-');

      mainValues.estimated_min = hourMin ? convertHrsMinsToMins(hourMin) : 0;
      mainValues.estimated_max = hourMax ? convertHrsMinsToMins(hourMax) : 0;
    } else {
      mainValues.estimated_min = 0;
      mainValues.estimated_max = 0;
    }

    delete mainValues.estimated_time_range;
  }

  if ('completed_at' in mainValues && mainValues.completed_at) {
    mainValues.completed_at = mainValues.completed_at.format('YYYY-MM-DD');
  }

  if ('target_date' in mainValues) {
    mainValues.target_date = mainValues.target_date && moment(mainValues.target_date).format('YYYY-MM-DD');
  }

  if ('uploads' in mainValues && Array.isArray(mainValues.uploads)) {
    mainValues.uploads = mainValues.uploads
      .filter((file: UploadFileApi) => file.response?.stated === false)
      .map((file: UploadFileApi) => file.response?.uuid);
  }

  if ('responsibles' in mainValues) {
    mainValues.responsibles = mainValues.responsibles ? [mainValues.responsibles] : [];
  }

  return mainValues;
};

export interface RequestFormHandle {
  getParsedFieldsValues: () => Record<string, any>;
}

export interface RequestFormProps {
  type: 'create' | 'view' | 'draft';
  className?: string;
  currentStatus?: string;
  request?: Record<string, any>;
  formBuilder?: FormBuilderData;
  listFormBuilder?: FormBuilderData[];
  projectUuid?: string;
  projectBillable?: boolean;
  pageUrl: string;
  classNameColFormQuestions?: string;
  resetForm?: {
    oldRequest: Record<string, any>;
    oldFormBuilder: FormBuilderData;
  };
  estimatedResetTrigger?: number;
  goBack?: boolean;
  disableFormAnswers?: boolean;
  hideHeader?: boolean;
  hideMetaRows?: boolean;
  debounceDelay?: number;
  onDiscard?: () => Promise<void>;
  onDraft?: (values: any) => Promise<Record<string, any>>;
  onSubmit?: (values: any) => Promise<Record<string, any>>;
  onUpdate?: (values: any) => Promise<Record<string, any>>;
  onAddedTimelog?: (timelog: any) => void;
  onChangeStatus?: (newStatus: string) => void;
  onOpenPreview?: (data: { client: string; owner: string; requestName: string; estimate: string; answers: Record<string, any> }) => void;
}

const RequestForm = forwardRef<RequestFormHandle, RequestFormProps>(function RequestForm(
  {
    type,
    className,
    currentStatus,
    request,
    formBuilder,
    listFormBuilder,
    projectUuid,
    projectBillable,
    pageUrl,
    classNameColFormQuestions = style.colFormQuestions,
    resetForm,
    estimatedResetTrigger,
    goBack = false,
    disableFormAnswers: propDisableFormAnswers = false,
    hideHeader = false,
    hideMetaRows = false,
    onDiscard,
    onDraft,
    onSubmit,
    onUpdate,
    onAddedTimelog,
    onChangeStatus,
    onOpenPreview,
    debounceDelay = 800,
  },
  ref,
) {
  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const listForms = useSelector((state: RootState) => state.resources.requestForms);
  const [saving, setSaving] = useState(false);
  const [editingNoDraft, setEditingNoDraft] = useState(false);
  const [selectedProjectUuid, setSelectedProjectUuid] = useState(projectUuid ?? request?.project?.uuid);
  const editorRefs = useRef<Record<string, RichTextEditorHandle>>({});
  const [formAnswerValues, setFormAnswerValues] = useState<any>(() => {
    if (!formBuilder) return undefined;

    const reduceFn = (acc: Record<string, any>, item: FormBuilderFormData) => {
      if (!('name' in item)) return acc;

      let newAcc = { ...acc, [item.name]: item.answer_delta ?? item.answer };

      if (item.type === 'textEditor') {
        if (item.answer_delta !== undefined && item.answer_delta !== null) {
          newAcc = { ...newAcc, [`${item.name}_delta`]: item.answer_delta };
        }
        if (item.answer !== undefined && item.answer !== null) {
          newAcc = { ...newAcc, [item.name]: item.answer };
        }
      }

      return newAcc;
    };

    if (formBuilder.form) {
      return formBuilder.form.reduce(reduceFn, {});
    }

    if (formBuilder.wizardForm) {
      return formBuilder.wizardForm.reduce((acc, wizardForm) => {
        return { ...acc, ...wizardForm.form.reduce(reduceFn, {}) };
      }, {});
    }

    return undefined;
  });
  const formData = useMemo(() => {
    return getRequestFormData({ type: editingNoDraft ? 'draft' : type, request, formData: formBuilder?.form });
  }, [formBuilder?.form, request, type, editingNoDraft]);

  const [relatedUsers, setRelatedUsers] = useState(() => {
    const responsibles = request?.related_users?.filter((el: any) => el.pivot.type === 'responsible') ?? [];
    const followers = request?.related_users?.filter((el: any) => el.pivot.type === 'follower') ?? [];

    return { responsibles, followers };
  });

  const [formMain] = Form.useForm();
  const [formAnswer] = Form.useForm();

  const debounceUpdate = useMemo(() => {
    return debounceAsync((onUpdate: () => Promise<void>) => {
      return onUpdate();
    }, debounceDelay);
  }, [debounceDelay]);

  const isFreelancer = user.contact.type === 'freelancer';

  const disableFormAnswers = (propDisableFormAnswers && !editingNoDraft) || isFreelancer;

  const onWrapDraft = async (answerValues: any) => {
    setSaving(true);

    try {
      const values = await formMain.validateFields();

      values.status = 'draft';

      await onDraft?.(parseDataToSave(values, answerValues, formBuilder, listForms));
    } catch (error) {
      formScrollToFieldOnError(formMain, error);
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const onWrapSubmit = async (answerValues: any) => {
    setSaving(true);

    if (type !== 'create') {
      delete answerValues.uploads;
    }

    try {
      const values = await formMain.validateFields();

      values.status = 'new';

      await onSubmit?.(parseDataToSave(values, answerValues, formBuilder, listForms));
    } catch (error) {
      formScrollToFieldOnError(formMain, error);
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const onWrapUpdate = async (
    showCancelButton = false,
    changedValues?: Record<string, any>,
    source: 'main' | 'answer' = 'main',
  ) => {
    if (!onUpdate) return;

    setSaving(true);

    debounceUpdate(async () => {
      try {
        const refs = Object.values(editorRefs.current);
        await Promise.all(refs.map((ref) => ref?.waitForUploads?.()));

        const allMainValues = await formMain.validateFields();
        const allAnswerValues = await getFormData(formAnswer);

        delete allMainValues.status;

        if (!editingNoDraft) {
          delete allAnswerValues.uploads;
        }

        let updatePayload: Record<string, any> = {};

        if (source === 'main') {
          updatePayload = parseDataToSave({ ...changedValues });
        } else {
          const updatedAnswers = {
            ...allAnswerValues,
            ...changedValues,
          };

          updatePayload = {
            description: JSON.stringify({
              ...formBuilder,
              form: formBuilder?.form && parseFormBuilderAnswers(updatedAnswers, formBuilder.form, listForms),
              wizardForm:
                formBuilder?.wizardForm &&
                formBuilder.wizardForm.map((item) => ({
                  ...item,
                  form: parseFormBuilderAnswers(updatedAnswers, item.form, listForms),
                })),
            }),
          };

          Object.values(fixedFields).forEach((field) => {
            const value = allAnswerValues?.[field.name];
            if (value !== undefined) {
              updatePayload[field.name] = value;
            }
          });

          updatePayload = parseDataToSave(updatePayload);
        }

        await onUpdate(updatePayload);

        if (updatePayload.uploads) {
          const currentUploads = formAnswer.getFieldValue('uploads');
          const updated = currentUploads.map((upload: UploadFileApi) => {
            if (upload.response && updatePayload.uploads.includes(upload.response.uuid)) {
              upload.response.stated = true;
              upload.status = 'done';
            }
            return { ...upload };
          });

          formAnswer.setFieldsValue({ uploads: updated });
        }

        if (editingNoDraft && showCancelButton) {
          setEditingNoDraft(false);
        }
      } catch (error) {
        asyncErrorHandler(error);
      } finally {
        setSaving(false);
      }
    });
  };

  const onWrapDiscard = async () => {
    setSaving(true);

    try {
      await onDiscard?.();
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const getNotDirtyKeys = (currentValues: Record<string, any>, initialValues: Record<string, any>) => {
      return Object.keys(currentValues).filter((key) => isEqual(currentValues[key], initialValues[key]));
    };

    if (resetForm) {
      formMain.resetFields(
        getNotDirtyKeys(formMain.getFieldsValue(), {
          ...resetForm.oldRequest,
          responsibles: resetForm.oldRequest.related_users.find((el: any) => el.pivot.type === 'responsible')?.uuid,
          followers: resetForm.oldRequest.related_users
            .filter((el: any) => el.pivot.type === 'follower')
            .map((el: any) => el.uuid),
        }),
      );

      const oldFormData = getRequestFormData({
        type: 'draft',
        request: resetForm.oldRequest,
        formData: resetForm.oldFormBuilder?.form,
      });

      if (oldFormData) {
        const mapAnswers = oldFormData.reduce((acc, item) => {
          if ('name' in item) {
            return {
              ...acc,
              [item.name]: parseFormItemAnswer(item),
            };
          }

          return acc;
        }, {} as Record<string, any>);

        const fieldsValue = formAnswer.getFieldsValue();

        const uploadValue = fieldsValue.uploads ?? [];

        delete fieldsValue.uploads;

        formAnswer.resetFields(getNotDirtyKeys(fieldsValue, mapAnswers));

        formAnswer.setFieldsValue({
          uploads: [
            ...uploadValue,
            ...mapModelToUploadFileApi(
              (request?.uploads ?? []).filter(
                (item: any) => !uploadValue.find((file: UploadFileApi) => file.response?.uuid === item.uuid),
              ),
            ),
          ],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, formAnswer, formMain, resetForm]);

  useLayoutEffect(() => {
    if (request?.status === 'completed') {
      formMain.resetFields(['completed_at']);
    }
  }, [formMain, request?.status]);

  useEffect(() => {
    if (estimatedResetTrigger) {
      formMain.resetFields(['estimated_time_range']);
    }
  }, [estimatedResetTrigger, formMain]);

  useEffect(() => {
    if (type === 'create' && projectBillable !== undefined) {
      formMain.setFieldsValue({ billable: projectBillable });
    }
  }, [projectBillable, type, formMain]);

  useLayoutEffect(() => {
    if (request?.related_users) {
      const responsibles = request.related_users.filter((el: any) => el.pivot.type === 'responsible');
      const followers = request.related_users.filter((el: any) => el.pivot.type === 'follower');

      setRelatedUsers({ responsibles, followers });
    }
  }, [request?.related_users]);

  useImperativeHandle(ref, () => ({
    getParsedFieldsValues() {
      return parseDataToSave(formMain.getFieldsValue());
    },
  }));

  return (
    <>
      <Row className={`flex-col ${className}`} wrap={false}>
        <Col flex="none">
          <Form
            form={formMain}
            initialValues={{
              ...request,
              billable: request?.billable ?? projectBillable ?? true,
              project_id: selectedProjectUuid,
              user_id: request?.requester?.user?.uuid,
              completed_at: request?.completed_at && moment.utc(request?.completed_at),
              responsibles: request?.related_users?.find((el: any) => el.pivot.type === 'responsible')?.uuid,
              followers: request?.related_users?.filter((el: any) => el.pivot.type === 'follower').map((el: any) => el.uuid),
            }}
            labelAlign="left"
            layout="horizontal"
            labelCol={{ style: { width: 110, flexShrink: 0 } }}
            onValuesChange={(changedValues, allValues) => {
              if ('status' in changedValues && Object.keys(changedValues).length === 1) {
                return;
              }

              const fieldsToSend: any = { ...changedValues };

              const responsibleChanged = 'responsibles' in changedValues;
              const followersChanged = 'followers' in changedValues;

              const responsibleUuid = allValues.responsibles || null;
              const followers: string[] = allValues.followers || [];

              if (responsibleChanged) {
                if (responsibleUuid) {
                  const newFollowers = followers.filter((f: any) => f !== responsibleUuid);
                  fieldsToSend.followers = newFollowers;

                  if (newFollowers.length !== followers.length) {
                    formAnswer.setFieldsValue({ followers: newFollowers });
                  }
                }
              }

              if (followersChanged) {
                if (responsibleUuid && followers.includes(responsibleUuid)) {
                  fieldsToSend.responsibles = null;
                  formAnswer.setFieldsValue({ responsibles: null });
                } else {
                  fieldsToSend.followers = followers;
                }
              }

              if (responsibleChanged || followersChanged) {
                if (!('responsibles' in fieldsToSend)) {
                  fieldsToSend.responsibles = allValues.responsibles ?? null;
                }
                if (!('followers' in fieldsToSend)) {
                  fieldsToSend.followers = followers;
                }
              }

              onWrapUpdate(false, fieldsToSend, 'main');
            }}
            className="relative"
          >
            {hideHeader && (
              <RequestFormHeader
                request={request}
                status={currentStatus}
                type={type}
                pageUrl={pageUrl}
                goBack={goBack}
                saving={saving}
                onChangeStatus={onChangeStatus}
              />
            )}

            {!hideMetaRows && (
              <>
                {type !== 'create' ? (
                  !projectUuid && (
                    <Row gutter={[8, 8]}>
                      <Col flex="auto">
                        <Form.Item label="Client" name="user_id" className="mb-s">
                          <LinkExternalIcon to={`?client_uuid=${request?.project?.customer?.uuid}`} className="secondary">
                            {request?.project?.customer?.business_name}
                          </LinkExternalIcon>
                        </Form.Item>
                      </Col>

                      {!isFreelancer && (
                        <Col>
                          <Link to={`${appRoutes.PROJECTS}/${selectedProjectUuid}/requests`}>Go to project</Link>
                        </Col>
                      )}
                    </Row>
                  )
                ) : (
                  <Form.Item
                    label="Plan"
                    name="project_id"
                    className="mb-s"
                    hidden={!!projectUuid}
                    rules={[{ required: true, message: 'Please enter the project' }]}
                  >
                    <ProjectSelect
                      onlyRetainer
                      onlyActive
                      onChange={(value, option) => {
                        setSelectedProjectUuid(value);
                        const selectedBillable = (option as any)?.extra?.billable;
                        formMain.setFieldsValue({ user_id: undefined, billable: selectedBillable ?? true });
                      }}
                    />
                  </Form.Item>
                )}

                <Row align="middle" gutter={[12, 0]} justify="space-between">
                  <Col flex="auto">
                    <Form.Item
                      label="Requested by "
                      name="user_id"
                      className="mb-s"
                      rules={type === 'create' ? [{ required: true, message: 'Please enter the requester' }] : undefined}
                    >
                      {type !== 'create' ? (
                        <Text type="secondary">
                          {request?.requester?.name ?? 'No one'} at {parseDateToUSFormat(request?.created_at, true)}
                        </Text>
                      ) : (
                        <SelectUserAvatar
                          placeholder={selectedProjectUuid ? 'Select a requester' : 'Select a project first'}
                          defaultOptions={
                            request?.requester
                              ? [
                                  {
                                    key: request.requester.uuid,
                                    value: request.requester.uuid,
                                    label: request.requester.name,
                                    user: request.requester,
                                  },
                                ]
                              : undefined
                          }
                          size="middle"
                          projectId={selectedProjectUuid}
                          disabled={!selectedProjectUuid}
                          mentionables
                          bordered
                        />
                      )}
                    </Form.Item>
                  </Col>

                  {request?.status === 'completed' && (
                    <Col>
                      <Form.Item label="Completed at" className="mb-s" labelCol={{ style: { flexShrink: 0 } }}>
                        <Text type="secondary">{parseDateToUSFormat(request.completed_at)}</Text>
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Row gutter={[12, 0]}>
                  <Col flex="auto" className="mb-s w-full lg-flex-1">
                    <SelectResponsible
                      label="Owner"
                      form={formMain}
                      state={relatedUsers}
                      finalProjectId={selectedProjectUuid}
                      setState={setRelatedUsers}
                      disabled={!selectedProjectUuid || isFreelancer}
                      bordered
                    />
                  </Col>

                  <Col flex="auto" className="mb-s w-full lg-flex-1" style={{ minWidth: 190 }}>
                    <SelectFollowers
                      label="Followers"
                      form={formMain}
                      state={relatedUsers}
                      finalProjectId={selectedProjectUuid}
                      setState={setRelatedUsers}
                      labelCol={{ style: { width: 110, flexShrink: 0 }, className: 'lg-w-auto' }}
                      disabled={!selectedProjectUuid || isFreelancer}
                      bordered
                    />
                  </Col>
                </Row>

                <Row align="middle" gutter={[12, 0]}>
                  <Col flex="auto" className="w-full md-w-auto" style={{ minWidth: 265 }}>
                    <FieldTimeRangeCustom
                      name="estimated_time_range"
                      label="Estimated time"
                      className="mb-s"
                      disabled={isFreelancer}
                    />
                  </Col>

                  <Col flex="none">
                    <Form.Item name="billable" valuePropName="checked" className="mb-s">
                      <Checkbox disabled={isFreelancer}>Billable</Checkbox>
                    </Form.Item>
                  </Col>

                  {type === 'view' && request ? (
                    <Col flex="none">
                      <Form.Item label="Spent" className="mb-s" labelCol={{ style: { flexShrink: 0 } }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Text type="secondary">{renderTime(convertMinsToHrsMins(request.time_spent))}</Text>

                          {!isFreelancer && <RequestTimerDropdown request={request} onAdded={onAddedTimelog} />}
                        </div>
                      </Form.Item>
                    </Col>
                  ) : undefined}

                  {type !== 'create' ? (
                    <Col flex="none">
                      <Form.Item label="Due date" className="mb-s" labelCol={{ style: { flexShrink: 0 } }}>
                        <Text type="secondary">
                          {request?.target_date ? parseDateToUSFormat(request.target_date, false, true) : 'no due date'}
                        </Text>
                      </Form.Item>
                    </Col>
                  ) : undefined}
                </Row>

                {type === 'view' && (
                  <Form.Item label="Request name" style={{ alignItems: 'start' }} className="mb-m">
                    <Text style={{ wordBreak: 'break-word' }} strong>
                      {request?.subject}x
                    </Text>
                  </Form.Item>
                )}

                <Divider style={{ margin: '6px 0' }} className="primary" />
              </>
            )}
          </Form>
        </Col>

        <Col className={classNameColFormQuestions}>
          <div style={{ padding: '0px 4px', position: 'relative' }}>
            {type === 'view' && !isFreelancer && (
              <Row gutter={[12, 12]} align="middle" style={{ position: 'absolute', right: 6, zIndex: 1000 }}>
                <Col flex="none" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <PrinterOutlined
                    onClick={async () => {
                      const answerValues = await getFormData(formAnswer);
                      delete answerValues.uploads;
                      onOpenPreview?.({
                        client: request?.project.customer.business_name ?? '',
                        owner: request?.related_users.find((user: any) => user.pivot.type === 'responsible').name ?? '',
                        requestName: request?.subject ?? '',
                        estimate: `${convertMinsToHrsMins(request?.estimated_min) || '--'} ~ ${convertMinsToHrsMins(request?.estimated_max) || '--'}`,
                        answers: answerValues,
                      });
                    }}
                    style={{ cursor: 'pointer', fontSize: 16 }}
                    className="primary"
                  />

                  {editingNoDraft ? (
                    <MdCancel
                      size={16}
                      className="primary"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        formAnswer.resetFields();
                        setEditingNoDraft(false);
                      }}
                    />
                  ) : (
                    <AiOutlineEdit
                      size={20}
                      style={{ cursor: 'pointer' }}
                      className="primary"
                      onClick={() => setEditingNoDraft(true)}
                    />
                  )}
                </Col>
              </Row>
            )}
            {formBuilder ? (
              <Form
                form={formAnswer}
                layout="vertical"
                colon={false}
                requiredMark={false}
                labelCol={{ style: { fontWeight: '600' } }}
                initialValues={formAnswerValues}
                onValuesChange={(changedValues, newValues) => {
                  const updatedValues = {
                    ...formAnswerValues,
                    ...newValues,
                  };
                  setFormAnswerValues(updatedValues);

                  const keys = Object.keys(changedValues);

                  if ((keys.length > 1 || keys[0] !== 'uploads') && formAnswerValues) {
                    onWrapUpdate(false, changedValues, 'answer');
                  }
                }}
              >
                {formBuilder.wizardForm && (
                  <FormBuilderWizardForm
                    form={formAnswer}
                    data={formBuilder.wizardForm}
                    formValues={formAnswerValues}
                    disabled={disableFormAnswers}
                  />
                )}

                {formData ? (
                  <>
                    <FormBuilderAnswers
                      form={formAnswer}
                      data={formData}
                      formValues={formAnswerValues}
                      listData={listFormBuilder}
                      disabled={disableFormAnswers}
                      onTextImageUpload={
                        onUpdate && !editingNoDraft
                          ? async (upload) => {
                              if (!upload.response) return undefined;

                              const data = await onUpdate?.({
                                uploads: [upload.response.uuid],
                              });

                              if (data && upload.url) {
                                upload.url = upload.url.replace('/tmp/', '/uploads/');

                                if (upload.response) {
                                  upload.response.url = upload.url;
                                  upload.response.stated = true;
                                }

                                const currentUploads = formAnswer.getFieldValue('uploads');

                                formAnswer.setFieldsValue({
                                  uploads: currentUploads ? [...currentUploads, upload] : [upload],
                                });

                                return upload.url;
                              }

                              return undefined;
                            }
                          : undefined
                      }
                    />

                    <FormItem data={{ ...fixedFields.uploads, answer: request?.uploads }}>
                      <FieldUpload
                        data={fixedFields.uploads}
                        form={formAnswer}
                        resource="request"
                        resourceIds={request ? [request.uuid] : undefined}
                        disabled={disableFormAnswers}
                        showDownloadIcon={!!onUpdate}
                        onRemove={(_, textEditorUpdated) => textEditorUpdated && onWrapUpdate()}
                        onWrapUpdate={onWrapUpdate}
                        onRequest={
                          !editingNoDraft && onUpdate
                            ? async (upload) => {
                                try {
                                  return await onUpdate({ uploads: [upload.uuid] });
                                } catch (error) {
                                  asyncErrorHandler(error);
                                }

                                return null;
                              }
                            : undefined
                        }
                      />
                    </FormItem>

                    {!isFreelancer && (
                      <FormButtons
                        form={formAnswer}
                        saving={saving}
                        onDraft={onDraft && onWrapDraft}
                        onSubmit={onSubmit && onWrapSubmit}
                        onDiscard={onDiscard && onWrapDiscard}
                      />
                    )}
                  </>
                ) : undefined}
              </Form>
            ) : (
              <Empty description="No form request" />
            )}
          </div>
        </Col>
      </Row>

    </>
  );
});

export default RequestForm;
